/**
 * displayCopyGuard — refuses internal engineering voice in copy a parent reads.
 *
 * WHY THIS EXISTS (KB-2665): `priceNote` is customer-facing copy — it clarifies a price to a
 * parent at the moment they decide to pay ("Discount for returning students"). Four notes on
 * Cary School of Gymnastics' `camp-option1` variation instead read as an engineer's note-to-self
 * about how the price was derived — "Full week (vendor tuition ladder; matches existing
 * week-length materialized offers)". "Vendor tuition ladder" describes OUR pipeline; "matches
 * existing week-length materialized offers" is internal vocabulary that means nothing to a parent.
 *
 * WHERE THE RULE IS ENFORCED. These notes are authored in two places, not one:
 *   1. by hand in the admin variation form — this module, wired into `ageGroupVariationSchema`;
 *   2. by one-off migration scripts under `scripts/camp-data/_migrations/` — which is how the
 *      Inspire Athletics note was authored (`kb-1920-…:54`). Those are Node, and consume the
 *      same marker list through `_migrations/_internalVoice.cjs`.
 * It is deliberately NOT enforced in `_reconcile/offerComputeCore.cjs`: that file is a
 * byte-faithful mirror of `functions/src/shared/offer/prepareOfferDataCore.ts`, and a rule placed
 * there would have to exist in two files required to stay identical.
 *
 * THE MARKERS LIVE IN ONE FILE — `displayCopyMarkers.json`, which the Node side reads too.
 * An earlier version of this module restated them, and a third copy in the verifier script was a
 * review finding: two literals for one fact is the very defect this ticket exists to fix.
 *
 * WHAT IT IS NOT. This is not a style linter and it does not rewrite copy. It rejects a specific,
 * narrow failure: text whose subject is our own data pipeline or an unresolved internal question,
 * rather than the family's purchase. Genuine notes must pass untouched — the whole live catalog
 * (113 distinct notes: "Member discount available", "Plus a $20 registration fee",
 * "$265-$395 depending on pick up time") is pinned as a negative control in the tests.
 */
import markerData from "./displayCopyMarkers.json";

/** A marker, its human-readable reason, and the family it belongs to. */
export interface InternalVoiceMarker {
  readonly pattern: RegExp;
  readonly reason: string;
  readonly family: string;
}

export const INTERNAL_VOICE_MARKERS: readonly InternalVoiceMarker[] = markerData.markers.map(
  (m) => ({ pattern: new RegExp(m.pattern, m.flags), reason: m.reason, family: m.family }),
);

export interface InternalVoiceFinding {
  /** The exact substring that tripped the marker, so an error message can quote it. */
  readonly match: string;
  readonly reason: string;
}

/**
 * Returns every internal-voice finding in `text`. Empty array = the text is fine to display.
 *
 * Blank/whitespace-only input returns no findings: blank is the CORRECT value for an optional
 * note (editorial principle 5 — an optional field stays blank unless the addition is additive),
 * so emptiness is never an error here.
 */
export function findInternalVoice(text: string | null | undefined): InternalVoiceFinding[] {
  const t = String(text ?? "");
  if (!t.trim()) return [];

  const findings: InternalVoiceFinding[] = [];
  for (const marker of INTERNAL_VOICE_MARKERS) {
    const m = t.match(marker.pattern);
    if (m) findings.push({ match: m[0], reason: marker.reason });
  }
  return findings;
}

/** Convenience predicate: is this text safe to show a parent? */
export function isDisplayableCopy(text: string | null | undefined): boolean {
  return findInternalVoice(text).length === 0;
}

/**
 * A single message naming what tripped and why, for a form-validation error. Quotes the offending
 * substring so the author can see precisely which words to remove rather than guessing.
 */
export function describeInternalVoice(findings: readonly InternalVoiceFinding[]): string {
  if (findings.length === 0) return "";
  const parts = findings.map((f) => `"${f.match}" — ${f.reason}`);
  return `This note is shown to families. Remove internal wording: ${parts.join("; ")}.`;
}
